import type { TargetTypeType } from './generated';

export const TARGET_TYPE = {
  WEB_APPLICATION: 'WEB_APPLICATION',
  EXTERNAL_NETWORK: 'EXTERNAL_NETWORK',
  AI_APPLICATION: 'AI_APPLICATION',
  INTERNAL_NETWORK: 'INTERNAL_NETWORK',
} as const satisfies Record<TargetTypeType, TargetTypeType>;

export type TargetTypeConstant = (typeof TARGET_TYPE)[keyof typeof TARGET_TYPE];

export const TARGET_TYPE_VALUES = [
  TARGET_TYPE.WEB_APPLICATION,
  TARGET_TYPE.EXTERNAL_NETWORK,
  TARGET_TYPE.AI_APPLICATION,
  TARGET_TYPE.INTERNAL_NETWORK,
] as const;

export type ApplicationTargetType =
  | typeof TARGET_TYPE.WEB_APPLICATION
  | typeof TARGET_TYPE.AI_APPLICATION;

export function isApplicationTargetType(
  targetType: TargetTypeType | undefined,
): targetType is ApplicationTargetType {
  return (
    targetType === TARGET_TYPE.WEB_APPLICATION ||
    targetType === TARGET_TYPE.AI_APPLICATION
  );
}

export type TargetTypeDefinition = {
  description: string;
  aliases: readonly string[];
};

export const TARGET_TYPE_DEFINITIONS: Record<
  TargetTypeType,
  TargetTypeDefinition
> = {
  WEB_APPLICATION: {
    description:
      'a web app, website, or HTTP/REST/GraphQL/SOAP API reachable over HTTP(S).',
    aliases: ['web application', 'web app', 'webapp', 'website', 'web service'],
  },
  AI_APPLICATION: {
    description:
      'an AI/LLM-backed application, chatbot, or agent (the vulnerability concerns AI-specific behavior, prompts, or an AI product surface).',
    aliases: [
      'ai application',
      'ai app',
      'llm application',
      'genai application',
      'ai agent',
      'chatbot',
    ],
  },
  EXTERNAL_NETWORK: {
    description:
      'internet-facing network infrastructure that is not a web application — open ports, exposed services, netblocks, perimeter hosts, DNS/mail/network appliances reachable from the internet.',
    aliases: [
      'external network',
      'external asset',
      'internet-facing',
      'internet facing',
      'perimeter network',
      'netblock',
    ],
  },
  INTERNAL_NETWORK: {
    description:
      'internal, non-internet-facing network infrastructure — internal hosts, Active Directory, internal services only reachable from inside a corporate network.',
    aliases: [
      'internal network',
      'internal host',
      'active directory',
      'domain controller',
      'internal asset',
    ],
  },
};
